var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../sutl';
import { TransportError } from './framework/Transport';
var ZgnCommand;
(function (ZgnCommand) {
    ZgnCommand["Down"] = "DN";
    ZgnCommand["Preset1"] = "G1";
    ZgnCommand["Preset2"] = "G2";
    ZgnCommand["Preset3"] = "G3";
    ZgnCommand["Query"] = "QY";
    ZgnCommand["Stop"] = "ST";
    ZgnCommand["Up"] = "UP";
})(ZgnCommand || (ZgnCommand = {}));
function isMechoNetZgnShadeControlConfig(config) {
    return config.type === 'mechoNetZgn';
}
function isMechoNetZgnShadePresetConfig(config) {
    return config.type === 'mechoNetZgn';
}
export class MechoNetZgn {
    constructor(socket, state, log) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.pollKeys = [];
        this.shadeControlConfigs = {};
        this.shadePresetConfigs = {};
        // controls
        this.shadeOperation = {
            set: (key, op, tags) => __awaiter(this, void 0, void 0, function* () {
                if (op.type === 'position') {
                    const config = this.shadeControlConfigs[key];
                    if (config === undefined) {
                        this.log.error(`missing shade control config for key = ${key}`);
                        return op;
                    }
                    const command = op.motion === 'close'
                        ? ZgnCommand.Down
                        : op.motion === 'open'
                            ? ZgnCommand.Up
                            : ZgnCommand.Stop;
                    if (command === ZgnCommand.Stop || config.behavior === 'pressToStart') {
                        if (op.event === 'press') {
                            yield this.socket.sendAsync(this.makeCommand(config, ZgnCommand.Stop), tags);
                            if (command !== ZgnCommand.Stop) {
                                yield this.socket.sendAsync(this.makeCommand(config, command), tags);
                            }
                        }
                    }
                    else {
                        // config.behavior === 'holdToMove'
                        if (op.event === 'press') {
                            yield this.socket.sendAsync(this.makeCommand(config, command), tags);
                        }
                        else {
                            yield this.socket.sendAsync(this.makeCommand(config, ZgnCommand.Stop), tags);
                        }
                    }
                }
                else {
                    this.log.error(`tilt operation not supported`);
                }
                return op;
            }),
            getKey: config => {
                if (!isMechoNetZgnShadeControlConfig(config)) {
                    this.log.error(`invalid shade config: ${JSON.stringify(config)}`);
                    return 'unknown';
                }
                const key = this.getKey(config);
                this.shadeControlConfigs[key] = Object.assign({}, config);
                return key;
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!isMechoNetZgnShadeControlConfig(config)) {
                    this.log.error(`invalid shade control config: ${JSON.stringify(config)}`);
                    return { address: 'incompatible' };
                }
                const key = this.getKey(config);
                const tags = [`shadeOperation.validate.${key}`];
                try {
                    yield this.sendQuery(config, tags);
                }
                catch (error) {
                    if (error instanceof TransportError && error.code === 'sendTimeout') {
                        return { address: 'missing' };
                    }
                    throw error;
                }
                return { address: 'ok' };
            }),
        };
        this.shadeOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return Object.fromEntries(Object.keys(this.shadeControlConfigs).map(key => [key, ['position']]));
            }),
        };
        this.shadePosition = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const config = this.shadeControlConfigs[key];
                if (config === undefined) {
                    this.log.error(`missing shade control config for key = ${key}`);
                    return 0;
                }
                return this.sendQuery(config, tags);
            }),
            range: { max: 255, min: 0, round: 1 },
        };
        this.shadePreset = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return key === this.lastPresetKey;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const config = this.shadePresetConfigs[key];
                if (config === undefined) {
                    this.log.error(`missing shade preset config for key = ${key}`);
                    return value;
                }
                this.lastPresetKey = key;
                for (const presetKey of Object.keys(this.shadePresetConfigs)) {
                    if (presetKey !== key) {
                        this.state.shadePreset.update(presetKey, false);
                    }
                }
                if (value) {
                    const command = config.preset === 1
                        ? ZgnCommand.Preset1
                        : config.preset === 2
                            ? ZgnCommand.Preset2
                            : ZgnCommand.Preset3;
                    yield this.socket.sendAsync(this.makeCommand(config, ZgnCommand.Stop), tags);
                    yield this.socket.sendAsync(this.makeCommand(config, command), tags);
                }
                return value;
            }),
            getKey: config => {
                if (!isMechoNetZgnShadePresetConfig(config)) {
                    this.log.error(`invalid shade config: ${JSON.stringify(config)}`);
                    return 'unknown';
                }
                const key = this.getKey(config);
                this.shadePresetConfigs[key] = Object.assign({}, config);
                return key;
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!isMechoNetZgnShadePresetConfig(config)) {
                    this.log.error(`invalid shade preset config: ${JSON.stringify(config)}`);
                    return { address: 'incompatible', preset: 'incompatible' };
                }
                const key = this.getKey(config);
                const tags = [`shadeOperation.validate.${key}`];
                const preset = config.preset >= 1 && config.preset <= 3 ? 'ok' : 'range';
                try {
                    yield this.sendQuery(config, tags);
                }
                catch (error) {
                    if (error instanceof TransportError && error.code === 'sendTimeout') {
                        return { address: 'missing', preset };
                    }
                    throw error;
                }
                return { address: 'ok', preset };
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (Object.keys(this.shadeControlConfigs).length === 0) {
                        yield this.sendQuery(undefined, ['poll']);
                    }
                    else {
                        if (this.pollKeys.length === 0) {
                            this.pollKeys.push(...Object.keys(this.shadeControlConfigs));
                        }
                        const key = this.pollKeys.pop();
                        if (key === undefined) {
                            this.log.error(`key should not be undefined`);
                            return;
                        }
                        const config = this.shadeControlConfigs[key];
                        if (config === undefined) {
                            this.log.error(`missing shade control config for key = ${key}`);
                            return;
                        }
                        const value = yield this.sendQuery(config, [
                            `shadePosition.get.${key}`,
                            'poll',
                        ]);
                        this.state.shadePosition.update(key, value);
                    }
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.lastPresetKey = undefined;
            this.pollKeys.length = 0;
            this.shadeControlConfigs = {};
            this.shadePresetConfigs = {};
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            // Send a broadcast query to all devices and see if we get any response.
            try {
                yield this.sendQuery(undefined, ['initialize']);
            }
            catch (error) {
                return 'verifyFailed';
            }
            return 'connected';
        });
    }
    // other class methods
    getKey(config) {
        let key = `${config.address.zone}.${config.address.group}.${config.address.node}`;
        if ('preset' in config)
            key += `.${config.preset}`;
        return key;
    }
    makeCommand(config, command) {
        var _a;
        //const [group, node, zone] = key.split('.'); // ignore preset (if present)
        //const address = `${group}.${node}.${zone}`; // reassemble address without preset
        const { group, node, zone } = (_a = config === null || config === void 0 ? void 0 : config.address) !== null && _a !== void 0 ? _a : { group: 0, node: 0, zone: 0 };
        const address = `${group}.${node}.${zone}`;
        return `#${address}.A=${command}`;
    }
    // Send a query and return the shade position from the first received response.
    sendQuery(config, tags) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            //const [group, node, zone] = key.split('.'); // ignore preset (if present)
            //const address = `${group}.${node}.${zone}`; // reassemble address without preset
            const { group, node, zone } = (_a = config === null || config === void 0 ? void 0 : config.address) !== null && _a !== void 0 ? _a : { group: 0, node: 0, zone: 0 };
            const address = `${group}.${node}.${zone}`;
            const cmd = ZgnCommand.Query;
            const regExp = new RegExp(`^\\!${Sutl.escapeRegExp(address)}\\.${cmd}=([0-9a-fA-F]+)\\(\\d+\\.\\d+\\)$`);
            const match = yield this.socket.send(this.makeCommand(config, cmd), regExp, tags, {
                // The API document says a query can take up to 15 seconds to respond. I'm hoping that
                // number is lower in practice.
                sendTimeoutMs: 5000,
            });
            return parseInt(match[1], 16);
        });
    }
}
// static properties
MechoNetZgn.driverType = 'socketDriver';
MechoNetZgn.key = '-NO0QAEpYv0zsvb6bhcU';
MechoNetZgn.socketOptions = [
    {
        commandExtractor: {
            terminators: ['\r'],
        },
        port: 4999,
        sendTerminator: '\r',
    },
];
